import Cookies from 'js-cookie';
import { ICOMX_ACCESS_TOKEN_HEADER } from 'src/sections/crs/patient/components/icomx/session';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';

export const COMX_EMBED_CONFIG_PATH = '/host-api/comx/embed/config';
export type IcomxResourceType = string;
export type IcomxTelecomSystem = 'email' | 'phone';

export interface IcomxSelectedTelecom {
  system: IcomxTelecomSystem;
  value: string;
}

export interface PrepareIcomxResourceLinkRequest {
  selectedTelecom: IcomxSelectedTelecom;
}

export interface IcomxResourceDescriptor {
  resourceType: IcomxResourceType;
  resourceId: string;
  selectedTelecom?: IcomxSelectedTelecom;
}

const getEncodedResourcePath = ({ resourceType, resourceId }: IcomxResourceDescriptor) =>
  `${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`;

export const getComxResourceLinkPath = ({ resourceType, resourceId }: IcomxResourceDescriptor) =>
  `/host-api/resources/${getEncodedResourcePath({ resourceType, resourceId })}/comx-link`;

export const ensureComxResourceLinkPath = ({ resourceType, resourceId }: IcomxResourceDescriptor) =>
  `${getComxResourceLinkPath({ resourceType, resourceId })}/ensure`;

export const syncComxResourceLinkPath = ({ resourceType, resourceId }: IcomxResourceDescriptor) =>
  `${getComxResourceLinkPath({ resourceType, resourceId })}/sync`;

export interface IcomxEmbedConfig {
  enabled: boolean;
  iframeUrl: string | null;
  iframeOrigin: string | null;
  tenantKey: string | null;
}

export interface IcomxResourceLink extends IcomxResourceDescriptor {
  resourceReferenceUri: string;
  hasMapping: boolean;
  comxContactUuid: string | null;
  status: string;
}

export interface EnsureIcomxResourceLinkResponse extends IcomxResourceDescriptor {
  resourceReferenceUri: string;
  comxContactUuid: string | null;
  status: string;
  created: boolean;
  contactReconciled?: boolean;
  selectedTelecomVerified?: boolean | null;
}

export async function getIcomxEmbedConfig(signal?: AbortSignal) {
  // Send only to the tenant-resolved Careflow host API, never to the chat iframe.
  return apiClient.get<IcomxEmbedConfig>(COMX_EMBED_CONFIG_PATH, {
    signal,
    headers: { [ICOMX_ACCESS_TOKEN_HEADER]: Cookies.get('accessCode') ?? '' },
  });
}

export async function getIcomxResourceLink(resource: IcomxResourceDescriptor) {
  return apiClient.get<IcomxResourceLink>(getComxResourceLinkPath(resource));
}

export async function ensureIcomxResourceLink(resource: IcomxResourceDescriptor) {
  const payload = resource.selectedTelecom
    ? { selectedTelecom: resource.selectedTelecom }
    : undefined;
  return apiClient.post<
    PrepareIcomxResourceLinkRequest | undefined,
    EnsureIcomxResourceLinkResponse
  >(ensureComxResourceLinkPath(resource), payload);
}

export async function syncIcomxResourceLink(resource: IcomxResourceDescriptor) {
  const payload = resource.selectedTelecom
    ? { selectedTelecom: resource.selectedTelecom }
    : undefined;
  return apiClient.post<
    PrepareIcomxResourceLinkRequest | undefined,
    EnsureIcomxResourceLinkResponse
  >(syncComxResourceLinkPath(resource), payload);
}
